import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Download, TrendingUp, ArrowUp, ArrowDown } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const TOOL_LABELS = {
  budget: "Budget Calculator",
  contract: "Contract Drafter",
  style: "Style Recommender",
  poster: "Event Poster",
  photo_booth: "AI Photo Booth",
};

export default function TopAiTools() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/ai-tool-usage")
      .then((r) => setData(r.data))
      .catch(() => setData({ totals: [], recent: [], grand_total: 0 }))
      .finally(() => setLoading(false));
  }, []);

  const downloadCsv = async () => {
    try {
      const r = await api.get("/admin/ai-tool-usage/export.csv", {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ai_tool_usage_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed — admin login required");
    }
  };

  const top3 = (data?.totals || []).slice(0, 3);
  const grandTotal = data?.grand_total || 0;
  const maxTotal = top3[0]?.total || 1;

  return (
    <motion.div
      data-testid="top-ai-tools-widget"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="glass-card p-6 border border-white/10 mb-6"
    >
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <Sparkles size={18} className="text-gold" />
          <div>
            <h4 className="text-white font-body text-sm font-medium">Top AI Tools</h4>
            <p className="text-white/30 text-[10px] font-body uppercase tracking-wider mt-0.5">
              {grandTotal.toLocaleString()} total invocations
            </p>
          </div>
        </div>
        <button
          data-testid="top-ai-tools-export-csv"
          onClick={downloadCsv}
          className="inline-flex items-center gap-1.5 text-gold/80 hover:text-gold text-[10px] font-body uppercase tracking-wider border border-gold/30 hover:border-gold/60 px-2.5 py-1 transition-colors"
        >
          <Download size={11} /> CSV
        </button>
      </div>

      {loading ? (
        <p data-testid="top-ai-tools-loading" className="text-white/30 text-xs font-body">Loading…</p>
      ) : top3.length === 0 ? (
        <div data-testid="top-ai-tools-empty" className="flex items-center gap-2 py-2">
          <TrendingUp size={14} className="text-white/20" />
          <p className="text-white/30 text-xs font-body">
            No tool usage yet. They'll appear once a VIP runs one.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="top-ai-tools-list">
          {top3.map((row, i) => {
            const successRate = row.total > 0
              ? Math.round((row.success / row.total) * 100) : 0;
            const widthPct = Math.round((row.total / maxTotal) * 100);
            return (
              <div key={row.tool} data-testid={`top-ai-tool-row-${row.tool}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-gold/60 font-body text-[10px] w-3">#{i + 1}</span>
                    <span className="text-white font-body text-xs">
                      {TOOL_LABELS[row.tool] || row.tool}
                    </span>
                    <TrendChip trend={row.trend} testId={`top-ai-tool-trend-${row.tool}`} />
                  </div>
                  <div className="flex items-center gap-3 text-[10px] font-body">
                    <span className="text-gold">{row.total.toLocaleString()}</span>
                    <span className="text-white/30">{successRate}% ok</span>
                  </div>
                </div>
                <div className="h-1 bg-white/5 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${widthPct}%` }}
                    transition={{ duration: 0.6, delay: i * 0.1 }}
                    className="h-full bg-gradient-to-r from-gold/60 to-gold"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}

function TrendChip({ trend, testId }) {
  if (!trend) return null;
  const { wow_pct } = trend;
  if (wow_pct === null || wow_pct === undefined) return null;
  if (wow_pct === "new") {
    return (
      <span
        data-testid={testId}
        className="inline-flex items-center text-[8px] font-body uppercase tracking-wider text-luxury-black bg-gold rounded-sm px-1 py-0.5 leading-none"
      >
        New
      </span>
    );
  }
  const up = wow_pct >= 0;
  const Arrow = up ? ArrowUp : ArrowDown;
  const color = up ? "text-emerald-400" : "text-rose-400";
  const sign = up ? "+" : "";
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-0.5 text-[10px] font-body ${color}`}
      title={`vs prior 7-day window`}
    >
      <Arrow size={10} strokeWidth={2.5} />
      {sign}{wow_pct}%
    </span>
  );
}
